var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"b0e0a51647c3f5322ae60c6c3341e58f4bea34ce"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";
import { isAxiosError } from "axios";

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Override the default Breadcrumbs integration to stop Sentry from wrapping
  // history.pushState/replaceState. On Chrome-for-iOS multiple history wrappers
  // (the browser's own + Clarity + Mixpanel + Sentry) form a recursion cycle
  // that overflows the stack (Sentry ALOESUITE-PROD-BR). All other default
  // integrations and error capture are kept; only navigation breadcrumbs are lost.
  integrations: [Sentry.breadcrumbsIntegration({ history: false })],

  // Block errors originating from browser extensions
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^app:\/\//i,
  ],

  // Ignore common non-actionable browser errors
  ignoreErrors: [
    /ResizeObserver loop/,
    "Non-Error promise rejection captured",
    /Object Not Found Matching Id/,
    // Browser-extension / third-party noise (not our code)
    /runtime\.sendMessage/,
    /browser\.storage\./,
    /is not a valid selector/,
    // Next.js router invariant on same-URL navigation (benign)
    /attempted to hard navigate to the same URL/,
    // Browser translation extensions (e.g. Google Translate) mutating the DOM
    // out from under React (Sentry ALOESUITE-PROD-CK/CM/BV/CW). Not our code.
    /Failed to execute 'removeChild' on 'Node'/,
    /Failed to execute 'insertBefore' on 'Node'/,
    /The object can not be found here/,
    // Globals injected by in-app browsers / extensions (Sentry MP/MV/…)
    /swbrowser is not defined/,
    /xbrowser is not defined/,
    /getAbstract/,
    /TTRCCallbacks/,
    // Benign Stripe.js teardown race (Sentry ALOESUITE-PROD-97): when the
    // PaymentElement times out we remount it (DEV-805 auto-retry) or the user
    // navigates away mid-load; Stripe destroys the old element and its own async
    // init callback then throws from selfDestruct. Unhandled internal Stripe
    // error, does not block payment; reduced at the source by the load-timeout work.
    /This Element has already been destroyed/,
  ],

  // Filter out HTTP errors handled by the app (4xx/5xx are tracked server-side)
  beforeSend(event, hint) {
    if (isAxiosError(hint.originalException)) {
      return null;
    }

    // Drop ALOESUITE-PROD-BR: an unactionable "Maximum call stack" crash isolated
    // to Chrome-for-iOS (CriOS) and the Google iOS in-app browser (GSA). Confirmed
    // a browser bug, not ours: it persisted after removing our Mixpanel/Sentry
    // history wrappers AND after gating Clarity off those UAs (crash rate unchanged,
    // Clarity provably absent). CriOS strips the error source so it is unattributable.
    // Gate on the UA so we never hide a genuine stack overflow on other browsers.
    const exception = hint.originalException;
    const messages = [
      exception instanceof Error ? exception.message : "",
      event.message ?? "",
      ...(event.exception?.values?.map((v) => v.value ?? "") ?? []),
    ];
    if (
      typeof navigator !== "undefined" &&
      /(CriOS|GSA)/.test(navigator.userAgent) &&
      messages.some((m) => /Maximum call stack size exceeded/i.test(m))
    ) {
      return null;
    }

    // Drop errors thrown by scripts injected by native in-app browsers (e.g. the
    // Facebook/Instagram Android performance logger): their frames live under
    // app:// and are not our code (Sentry ALOESUITE-PROD-GN/H1/MQ, "Java object
    // is gone" / sendDataToNative). denyUrls(app://) misses these because Sentry
    // keys the filter URL off the page, not the injected frame, so match frames.
    const hasInjectedFrame = (event.exception?.values ?? []).some((value) =>
      (value.stacktrace?.frames ?? []).some((frame) =>
        /^app:\/\/|navigation_performance_logger|sendDataToNative/.test(
          frame.filename ?? ""
        )
      )
    );
    if (hasInjectedFrame) {
      return null;
    }

    // Drop the minified CriOS/GSA error family (Sentry "Error: Ba/ga/fa/Ca",
    // ALOESUITE-PROD-M1/MG/MS/…): unsymbolicated 2-3 char messages seen only on
    // Chrome-for-iOS / Google in-app, spread across unrelated pages — the same
    // browser-bug profile as BR above. Gate on the UA and on a short minified
    // token so a real short error on any other browser is never hidden.
    if (
      typeof navigator !== "undefined" &&
      /(CriOS|GSA)/.test(navigator.userAgent) &&
      messages.some((m) => /^[A-Za-z]{2,3}$/.test(m.trim()))
    ) {
      return null;
    }

    return event;
  },
});
